import { WatermarkProtectionLayer, SecurityEvent } from './watermark-protection';
import { AntiInspectLayer } from './anti-inspect';

export interface WatermarkConfig {
  layer1: {
    enabled: boolean;
    opacity: number;
    animationSpeed: number;
    pattern: 'random' | 'smooth' | 'bouncing';
    watermarkImage: string;
    showUserId: boolean;
    showTimestamp: boolean;
  };

  protection: {
    enabled: boolean;
    autoReinstate: boolean;
    strictMode: boolean;
    checkInterval: number;
  };

  antiInspect: {
    enabled: boolean;
  };

  user: {
    id: string;
    displayName: string;
    sessionId: string;
  };
}

export interface BackendWatermarkCommand {
  type: 'update_config' | 'toggle_layer1' | 'emergency_shutdown' | 'increase_security';
  config?: Partial<WatermarkConfig>;
  timestamp: number;
}

export class WatermarkConfigManager {
  private config: WatermarkConfig;
  private protectionLayer: WatermarkProtectionLayer | null = null;
  private antiInspectLayer: AntiInspectLayer | null = null;
  private securityEventCallback: ((event: SecurityEvent) => void) | null = null;
  private commandCallback: ((command: BackendWatermarkCommand) => void) | null = null;
  private isInitialized: boolean = false;

  constructor(initialConfig: Partial<WatermarkConfig> = {}) {
    this.config = this.getDefaultConfig();
    this.config = { ...this.config, ...initialConfig };
  }

  private getDefaultConfig(): WatermarkConfig {
    return {
      layer1: {
        enabled: true,
        opacity: 0.8,
        animationSpeed: 1.0,
        pattern: 'smooth',
        watermarkImage: '',
        showUserId: true,
        showTimestamp: true,
      },
      protection: {
        enabled: true,
        autoReinstate: true,
        strictMode: true,
        checkInterval: 1000,
      },
      antiInspect: {
        enabled: true,
      },
      user: {
        id: '',
        displayName: '',
        sessionId: this.generateSessionId(),
      },
    };
  }

  public initialize(
    protectedElements: HTMLElement[],
    securityCallback?: (event: SecurityEvent) => void,
    commandCallback?: (command: BackendWatermarkCommand) => void
  ): void {
    if (this.isInitialized) {
      return;
    }

    this.securityEventCallback = securityCallback || null;
    this.commandCallback = commandCallback || null;

    if (this.config.protection.enabled) {
      this.protectionLayer = new WatermarkProtectionLayer({
        enabled: true,
        autoReinstate: this.config.protection.autoReinstate,
        strictMode: this.config.protection.strictMode,
        checkInterval: this.config.protection.checkInterval,
        reportToBackend: true,
      });

      this.protectionLayer.initialize(
        protectedElements,
        this.config.user.id,
        (event) => this.handleSecurityEvent(event)
      );
    }

    if (this.config.antiInspect.enabled) {
      this.antiInspectLayer = new AntiInspectLayer({
        enabled: true,
        redirectUrl: 'about:blank',
        onDetection: (type, details) => this.handleAntiInspectDetection(type, details),
      });
    }

    this.isInitialized = true;
  }

  private handleSecurityEvent(event: SecurityEvent): void {
    if (this.securityEventCallback) {
      this.securityEventCallback(event);
    }

    if (event.severity === 'critical' && this.config.protection.strictMode) {
      this.handleCriticalSecurityEvent(event);
    }
  }

  private handleCriticalSecurityEvent(event: SecurityEvent): void {
    if (this.commandCallback) {
      this.commandCallback({
        type: 'increase_security',
        timestamp: Date.now(),
      });
    }
  }

  private handleAntiInspectDetection(type: string, details: string): void {
    const event: SecurityEvent = {
      eventType: 'devtools_detected',
      userId: this.config.user.id,
      timestamp: Date.now(),
      sessionId: this.config.user.sessionId,
      details: `${type}: ${details}`,
      severity: 'high',
    };

    this.handleSecurityEvent(event);
  }

  public processBackendCommand(command: BackendWatermarkCommand): void {
    switch (command.type) {
      case 'update_config':
        if (command.config) {
          this.updateConfig(command.config);
        }
        break;

      case 'toggle_layer1':
        this.toggleLayer1();
        break;

      case 'emergency_shutdown':
        this.emergencyShutdown();
        break;

      case 'increase_security':
        this.increaseSecurity();
        break;
    }
  }

  public updateConfig(newConfig: Partial<WatermarkConfig>): void {
    this.config = this.deepMerge(this.config, newConfig);

    if (this.protectionLayer && newConfig.protection) {
      this.protectionLayer.updateConfig({
        enabled: this.config.protection.enabled,
        autoReinstate: this.config.protection.autoReinstate,
        strictMode: this.config.protection.strictMode,
        checkInterval: this.config.protection.checkInterval,
        reportToBackend: true,
      });
    }

    if (this.antiInspectLayer && newConfig.antiInspect) {
      this.antiInspectLayer.updateConfig({
        enabled: this.config.antiInspect.enabled,
      });
    }
  }

  private toggleLayer1(): void {
    this.config.layer1.enabled = !this.config.layer1.enabled;
  }

  private emergencyShutdown(): void {
    this.config.layer1.enabled = false;

    if (this.commandCallback) {
      this.commandCallback({
        type: 'emergency_shutdown',
        timestamp: Date.now(),
      });
    }
  }

  private increaseSecurity(): void {
    this.config.protection.strictMode = true;
    this.config.protection.checkInterval = 500;

    if (this.protectionLayer) {
      this.protectionLayer.updateConfig({
        strictMode: true,
        checkInterval: 500,
      });
    }
  }

  public getConfig(): WatermarkConfig {
    return { ...this.config };
  }

  public getStatistics(): {
    protection?: any;
    antiInspect?: any;
  } {
    return {
      protection: this.protectionLayer?.getStatistics(),
      antiInspect: this.antiInspectLayer?.getStatistics(),
    };
  }

  public monitorPiPEvents(videoElement: HTMLVideoElement): void {
    if (this.protectionLayer) {
      this.protectionLayer.monitorPiPEvents(videoElement);
    }
  }

  public destroy(): void {
    if (this.protectionLayer) {
      this.protectionLayer.destroy();
      this.protectionLayer = null;
    }

    if (this.antiInspectLayer) {
      this.antiInspectLayer.destroy();
      this.antiInspectLayer = null;
    }

    this.isInitialized = false;
  }

  private deepMerge(target: any, source: any): any {
    const output = { ...target };
    
    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach((key) => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = this.deepMerge(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    
    return output;
  }

  private isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

